import { BaseDataProvider } from "./baseProvider";
import type {
  ProviderListParams,
  UpdateProviderStatusDto,
  CreateConfigurationDto,
  UpdateConfigurationDto,
  UpdateBusinessRulesDto,
  Environment,
} from "../types/api";

export class ProviderDataProvider extends BaseDataProvider {
  constructor() {
    super("providers");
  }

  async getList(_resource: string, params: ProviderListParams) {
    const { page, perPage } = params.pagination || { page: 1, perPage: 20 };
    const { field, order } = params.sort || {
      field: "createdAt",
      order: "DESC",
    };
    const filter = params.filter || {};

    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: perPage.toString(),
    });

    if (field && order) {
      queryParams.append("sortBy", field);
      queryParams.append("sortOrder", order.toUpperCase());
    }

    if (filter.search) {
      queryParams.append("search", filter.search);
    }
    if (filter.status) {
      queryParams.append("status", filter.status);
    }
    if (filter.type) {
      queryParams.append("type", filter.type);
    }

    const endpoint = `providers?${queryParams.toString()}`;
    const response = (await this.makeRequest(endpoint)) as {
      data?: unknown[];
      providers?: unknown[];
      total?: number;
      totalCount?: number;
      pageInfo?: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      };
    };

    const dataArray = response.data || response.providers || [];
    const total = response.total || response.totalCount || 0;

    return {
      data: dataArray,
      total: total,
      pageInfo: {
        hasNextPage: response.pageInfo?.hasNextPage || false,
        hasPreviousPage: response.pageInfo?.hasPreviousPage || false,
      },
    };
  }

  async getOne(_resource: string, params: Record<string, unknown>) {
    const endpoint = `providers/${params.id}`;
    const response = await this.makeRequest(endpoint);
    return { data: response };
  }

  async getMany(resource: string, params: Record<string, unknown>) {
    const promises = (params.ids as string[]).map((id) =>
      this.getOne(resource, { id }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async getManyReference() {
    return {
      data: [],
      total: 0,
      pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }

  async create(_resource: string, params: Record<string, unknown>) {
    const endpoint = "providers";
    const response = await this.makeRequest(endpoint, {
      method: "POST",
      body: JSON.stringify(params.data),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return { data: response };
  }

  async update(_resource: string, params: Record<string, unknown>) {
    const endpoint = `providers/${params.id}`;
    const response = await this.makeRequest(endpoint, {
      method: "PUT",
      body: JSON.stringify(params.data),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return { data: response };
  }

  async updateMany(_resource: string, params: Record<string, unknown>) {
    const promises = (params.ids as string[]).map((id) =>
      this.update(_resource, { id, data: params.data }),
    );
    const results = await Promise.all(promises);
    return { data: results.map((result) => result.data) };
  }

  async delete(_resource: string, params: Record<string, unknown>) {
    const endpoint = `providers/${params.id}`;
    await this.makeRequest(endpoint, {
      method: "DELETE",
    });
    return { data: params.id };
  }

  async deleteMany(_resource: string, params: Record<string, unknown>) {
    const promises = (params.ids as string[]).map((id) =>
      this.delete(_resource, { id }),
    );
    await Promise.all(promises);
    return { data: params.ids };
  }

  // Custom methods for provider-specific operations
  async updateStatus(providerId: string, statusData: UpdateProviderStatusDto) {
    const endpoint = `providers/${providerId}/status`;
    const response = await this.makeRequest(endpoint, {
      method: "PUT",
      body: JSON.stringify(statusData),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return response;
  }

  async getConfigurations(providerId: string, environment?: Environment) {
    const endpoint = environment
      ? `providers/${providerId}/config?environment=${environment}`
      : `providers/${providerId}/config`;
    const response = await this.makeRequest(endpoint);
    return response;
  }

  async addConfiguration(
    providerId: string,
    configData: CreateConfigurationDto,
  ) {
    const endpoint = `providers/${providerId}/config`;
    const response = await this.makeRequest(endpoint, {
      method: "POST",
      body: JSON.stringify(configData),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return response;
  }

  async updateConfiguration(
    providerId: string,
    key: string,
    configData: UpdateConfigurationDto,
  ) {
    const endpoint = `providers/${providerId}/config/${key}`;
    const response = await this.makeRequest(endpoint, {
      method: "PUT",
      body: JSON.stringify(configData),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return response;
  }

  async deleteConfiguration(
    providerId: string,
    key: string,
    environment?: Environment,
  ) {
    const endpoint = environment
      ? `providers/${providerId}/config/${key}?environment=${environment}`
      : `providers/${providerId}/config/${key}`;
    await this.makeRequest(endpoint, {
      method: "DELETE",
    });
    return { success: true };
  }

  async getBusinessRules(providerId: string, environment?: Environment) {
    const endpoint = environment
      ? `providers/${providerId}/business-rules?environment=${environment}`
      : `providers/${providerId}/business-rules`;
    const response = await this.makeRequest(endpoint);
    return response;
  }

  async updateBusinessRules(
    providerId: string,
    rulesData: UpdateBusinessRulesDto,
  ) {
    const endpoint = `providers/${providerId}/business-rules`;
    const response = await this.makeRequest(endpoint, {
      method: "PUT",
      body: JSON.stringify(rulesData),
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    });
    return response;
  }

  async testConnection(providerId: string) {
    const endpoint = `providers/${providerId}/test-connection`;
    const response = await this.makeRequest(endpoint, {
      method: "POST",
    });
    return response;
  }

  async getHealth(providerId: string) {
    const endpoint = `providers/${providerId}/health`;
    const response = await this.makeRequest(endpoint);
    return response;
  }

  /**
   * Deactivates a provider's business rules for a specific environment
   * (defaults to production if omitted). Static like syncRateHawkStaticData
   * — called directly from components outside the react-admin dataProvider
   * registry, not through an instance.
   */
  static async deactivateProvider(
    providerId: string,
    environment?: Environment,
  ) {
    const { apiUrl, httpClient } = await import("../httpClient");
    const endpoint = environment
      ? `/providers/${providerId}?environment=${environment}`
      : `/providers/${providerId}`;
    await httpClient(apiUrl(endpoint), { method: "DELETE" });
    return { success: true };
  }

  // Static method for RateHawk sync (not resource-specific)
  static async syncRateHawkStaticData() {
    const { apiUrl, httpClient } = await import("../httpClient");
    const url = apiUrl("/providers/ratehawk/sync-static-data");
    const { json } = await httpClient(url, {
      method: "POST",
    });
    return json;
  }
}
